import type { ReactNode } from 'react'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
  type SheetContentProps,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useStore, type AnyFormApi } from '@tanstack/react-form'
import { useBlocker } from '@tanstack/react-router'
import { ORDER_ASIDE_SHEET_CONTENT_CLASS } from './order-aside.styles'

type OrderAsideProps = {
  children?: ReactNode
  className?: string
  description?: string
  onCloseAnimationEnd?: () => void
  onOpenAutoFocus?: SheetContentProps['onOpenAutoFocus']
  onOpenChange: (open: boolean) => void
  open: boolean
  title?: string
}

export function OrderAside({
  children,
  className,
  description = 'View and manage order details and actions',
  onCloseAnimationEnd,
  onOpenAutoFocus,
  onOpenChange,
  open,
  title = 'Order Actions',
}: OrderAsideProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className={cn(ORDER_ASIDE_SHEET_CONTENT_CLASS, className)}
        onAnimationEnd={(event) => {
          if (
            event.target === event.currentTarget &&
            event.currentTarget.dataset.state === 'closed'
          ) {
            onCloseAnimationEnd?.()
          }
        }}
        onOpenAutoFocus={onOpenAutoFocus}
      >
        <div className="sr-only">
          <SheetTitle>{title}</SheetTitle>
          <SheetDescription>{description}</SheetDescription>
        </div>
        {children}
      </SheetContent>
    </Sheet>
  )
}

type OrderAsideFormFooterProps = {
  /** The aside's form: the footer reads whether it changed and whether it is submitting. */
  form: Pick<AnyFormApi, 'store'>
  mutation: { isPending: boolean }
  saveLabel: string
  onCancel?: () => void
}

export function OrderAsideFormFooter({
  form,
  mutation,
  saveLabel,
  onCancel,
}: OrderAsideFormFooterProps) {
  const isDefaultValue = useStore(form.store, (state) => state.isDefaultValue)
  const isSubmitting = useStore(form.store, (state) => state.isSubmitting)
  const hasUnsavedChanges = !isDefaultValue
  const isSaving = isSubmitting || mutation.isPending

  useBlocker({
    shouldBlockFn: ({ next }) => {
      if (!hasUnsavedChanges) return false
      if (next.fullPath === '/login') return false

      const shouldLeave = confirm('You have unsaved changes. Are you sure you want to leave?')
      return !shouldLeave
    },
    enableBeforeUnload: hasUnsavedChanges,
  })

  return (
    <div className="shrink-0 px-5 pt-4 pb-5 sm:px-6">
      <div className="flex flex-wrap items-center justify-end gap-2">
        <Button type="button" variant="outline" disabled={isSaving} onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={isSaving}>
          {isSaving ? 'Saving...' : saveLabel}
        </Button>
      </div>
    </div>
  )
}
